import { Navigate } from 'react-router-dom';

import ClientGroupPage from '@p/security/users/client-group/client-group-page';
import DashBoardPage from '@p/security/users/dashboard/dashboard.page';

import { NonAuthLayout, VerticalLayout } from 'components/layouts';
import { paths } from 'constants/paths';
import { changePasswordAction } from 'pages/authentication/change-password/actions';
import { forgotAction, ForgotPasswordPage } from 'pages/authentication/forgot-password';
import { loginAction, LoginPage } from 'pages/authentication/login';
import { LogoutPage } from 'pages/authentication/logout';
import { profileAction } from 'pages/authentication/profile/actions';
import { registerAction, RegisterPage } from 'pages/authentication/register';
import { NotFoundPage, ServerErrorPage } from 'pages/common';
import { analyticsRoute } from 'routes/analytics-routes';
import { IRoutes } from 'types/routes';
import { lazyNamed } from 'utilities/lazy-named';
import { s } from 'utilities/strings';

import { abRoutes } from './audience-builder-routes';
import { utilityRoutes } from './utility-routes';

const ChangePasswordPage = lazyNamed(() => import('pages/authentication/change-password'), 'ChangePasswordPage');
const DashboardPage = lazyNamed(() => import('pages/dashboard'), 'DashboardPage');
const IntegrationsPage = lazyNamed(() => import('pages/integrations'), 'IntegrationsPage');
const RouteGuard = lazyNamed(() => import('hoc/route-guard'), 'RouteGuard');
const SecurityPage = lazyNamed(() => import('pages/security'), 'SecurityPage');
const UserProfilePage = lazyNamed(() => import('pages/authentication/profile'), 'UserProfilePage');
const WorkflowPage = lazyNamed(() => import('pages/workflow'), 'WorkflowPage');

export const routes: IRoutes = {
  nonAuthLayout: {
    children: [
      {
        element: <ServerErrorPage />,
        path: 'Root.children.Error',
      },
      {
        action: forgotAction,
        element: <ForgotPasswordPage />,
        path: 'Root.children.ForgotPassword',
      },
      {
        action: loginAction,
        element: <LoginPage />,
        path: 'Root.children.Login',
      },
      {
        element: <LogoutPage />,
        path: 'Root.children.Logout',
      },
      {
        action: registerAction,
        element: <RegisterPage />,
        path: 'Root.children.Register',
      },
      {
        element: <NotFoundPage />,
        path: 'Root.children.NotFound',
      },
    ],
    element: <NonAuthLayout />,
    path: 'Root.path',
  },
  verticalLayout: {
    children: [
      // dashboard
      {
        children: [
          {
            element: <DashboardPage />,
            index: true,
          },
        ],
        element: <RouteGuard role={['admin']} />,
        icon: ['fas', 'display'],
        includeInMenu: true,
        path: 'Root.children.Dashboard',
        roles: ['admin'],
        title: s('Site.Dashboard'),
      },
      // analytics
      analyticsRoute,
      // audience builder
      abRoutes,
      // integrations
      {
        children: [
          {
            element: <IntegrationsPage />,
            index: true,
          },
          {
            children: [
              {
                element: <IntegrationsPage />,
                index: true,
              },
            ],
            element: <RouteGuard role={['admin']} />,
            icon: ['fas', 'globe'],
            includeInMenu: true,
            path: 'Root.children.Integrations.children.Acxiom',
            roles: ['admin'],
            title: s('Site.Pages.Integrations.Acxiom'),
          },
          {
            children: [
              {
                element: <IntegrationsPage />,
                index: true,
              },
            ],
            element: <RouteGuard role={['admin']} />,
            icon: ['fas', 'landmark-dome'],
            includeInMenu: true,
            path: 'Root.children.Integrations.children.L2',
            roles: ['admin'],
            title: s('Site.Pages.Integrations.L2'),
          },
          {
            children: [
              {
                element: <IntegrationsPage />,
                index: true,
              },
            ],
            element: <RouteGuard role={['admin']} />,
            icon: ['fas', 'table'],
            includeInMenu: true,
            path: 'Root.children.Integrations.children.Airtable',
            roles: ['admin'],
            title: s('Site.Pages.Integrations.Airtable'),
          },
          {
            children: [
              {
                element: <IntegrationsPage />,
                index: true,
              },
            ],
            element: <RouteGuard role={['admin']} />,
            icon: ['fas', 'receipt'],
            includeInMenu: true,
            path: 'Root.children.Integrations.children.Intacct',
            roles: ['admin'],
            title: s('Site.Pages.Integrations.Intacct'),
          },
        ],
        element: <RouteGuard role={['admin']} />,
        icon: ['fas', 'arrows-alt-h'],
        includeInMenu: true,
        path: 'Root.children.Integrations.path',
        roles: ['admin'],
        title: s('Site.Integrations'),
      },
      // workflow
      {
        children: [
          {
            element: <WorkflowPage />,
            index: true,
          },
          {
            children: [
              {
                element: <WorkflowPage />,
                index: true,
              },
            ],
            element: <RouteGuard role={['admin']} />,
            icon: ['fas', 'envelopes-bulk'],
            includeInMenu: true,
            path: 'Root.children.Workflow.children.Studio',
            roles: ['admin'],
            title: s('Site.Studio'),
          },
          {
            children: [
              {
                element: <WorkflowPage />,
                index: true,
              },
            ],
            element: <RouteGuard role={['admin']} />,
            icon: ['fas', 'user-tie'],
            includeInMenu: true,
            path: 'Root.children.Workflow.children.CIS',
            roles: ['admin'],
            title: s('Site.CIS'),
          },
          {
            children: [
              {
                element: <WorkflowPage />,
                index: true,
              },
            ],
            element: <RouteGuard role={['admin']} />,
            icon: ['fas', 'group-arrows-rotate'],
            includeInMenu: true,
            path: 'Root.children.Workflow.children.MergePurge',
            roles: ['admin'],
            title: s('Site.MergePurge'),
          },
        ],
        element: <RouteGuard role={['admin']} />,
        icon: ['fas', 'envelope-open-text'],
        includeInMenu: true,
        path: 'Root.children.Workflow.path',
        roles: ['admin'],
        title: s('Site.Workflow'),
      },
      // utilities
      utilityRoutes,
      // security
      {
        children: [
          {
            element: <SecurityPage />,
            index: true,
          },
          {
            children: [
              {
                element: <DashBoardPage />,
                index: true,
              },
            ],
            element: <RouteGuard role={['admin']} />,
            icon: ['fas', 'user-group'],
            includeInMenu: true,
            path: 'Root.children.Security.children.Users',
            roles: ['admin'],
            title: s('Site.Pages.Security.Users'),
          },
          {
            children: [
              {
                element: <ClientGroupPage />,
                index: true,
              },
            ],
            element: <RouteGuard role={['admin']} />,
            icon: ['fas', 'people-group'],
            includeInMenu: true,
            path: 'Root.children.Security.children.Groups',
            roles: ['admin'],
            title: s('Site.Pages.Security.Groups'),
          },
        ],
        element: <RouteGuard role={['admin']} />,
        icon: ['fas', 'lock'],
        includeInMenu: true,
        path: 'Root.children.Security.path',
        roles: ['admin'],
        title: s('Site.Security'),
      },
      // user
      {
        children: [
          {
            element: (
              <Navigate
                replace
                to={paths.Root.children.User.children.Profile}
              />
            ),
            index: true,
          },
          // change password
          {
            children: [
              {
                action: changePasswordAction,
                element: <ChangePasswordPage />,
                index: true,
              },
            ],
            element: <RouteGuard />,
            path: 'Root.children.User.children.ChangePassword',
          },
          // profile page
          {
            children: [
              {
                action: profileAction,
                element: <UserProfilePage />,
                index: true,
              },
            ],
            element: <RouteGuard />,
            path: 'Root.children.User.children.Profile',
          },
        ],
        path: 'Root.children.User.path',
      },
    ],
    element: <VerticalLayout />,
    path: 'Root.path',
  },
};
